/**
 * Horizontal ranking bars (spec section 17: protein-per-dollar ranking,
 * price-per-100 g comparison, value-score ranking).
 *
 * Single series → no legend. Every bar is directly labelled with its value, so
 * the encoding is never colour-alone. Bars have 4px rounded data-ends anchored
 * to the baseline; products missing the metric are listed below the chart with
 * a reason rather than drawn as zero-length bars.
 */

import { useId } from 'react';
import type { MetricId } from '@/lib/nutrition/calculations';
import { METRICS, METRIC_DIRECTION } from '@/lib/nutrition/calculations';
import type { AnalysedProduct } from '@/lib/nutrition/ranking';
import { formatMetricValue, metricUnit } from '@/lib/format';

interface Props {
  rows: AnalysedProduct[];
  metric: MetricId | 'valueScore';
  maxBars?: number;
  highlightId?: string | null;
}

const ROW_H = 26;
const LABEL_W = 130;

export function RankingBarChart({
  rows,
  metric,
  maxBars = 8,
  highlightId = null,
}: Props): React.JSX.Element {
  const titleId = useId();
  const label = metric === 'valueScore' ? 'Value score' : METRICS[metric].label;
  const direction = metric === 'valueScore' ? 'higher' : METRIC_DIRECTION[metric];

  const read = (row: AnalysedProduct): number | null =>
    metric === 'valueScore' ? row.score.score : (row.metrics.values[metric]?.value ?? null);

  const withValue = rows
    .map((row) => ({ row, value: read(row) }))
    .filter((e): e is { row: AnalysedProduct; value: number } => e.value !== null)
    .sort((a, b) => (direction === 'higher' ? b.value - a.value : a.value - b.value))
    .slice(0, maxBars);

  const missing = rows.length - withValue.length;

  if (withValue.length === 0) {
    return (
      <p className="chart-empty">
        No product in these results has a value for {label.toLowerCase()}.
      </p>
    );
  }

  const currency = rows[0]?.metrics.currency ?? null;
  const width = 520;
  const height = withValue.length * ROW_H + 8;
  // Bars are always drawn from a zero baseline; for lower-is-better metrics the
  // shortest bar is the winner, which the rank number and label make explicit.
  const maxValue = Math.max(...withValue.map((e) => e.value));
  const barMax = width - LABEL_W - 74;

  return (
    <figure className="chart-figure">
      <svg
        className="chart-svg"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-labelledby={titleId}
      >
        <title id={titleId}>
          {label} ranking. Best: {withValue[0]!.row.product.name}.
        </title>
        {withValue.map((entry, i) => {
          const y = i * ROW_H + 4;
          const barW = maxValue === 0 ? 0 : Math.max(2, (entry.value / maxValue) * barMax);
          const isHighlight = entry.row.product.id === highlightId;
          return (
            <g key={entry.row.product.id}>
              <text className="chart-label" x={0} y={y + 13} fill="var(--text-secondary)">
                {truncate(entry.row.product.name, 20)}
              </text>
              <rect
                x={LABEL_W}
                y={y + 3}
                width={barW}
                height={14}
                rx={4}
                fill={i === 0 || isHighlight ? 'var(--accent)' : 'var(--series-protein)'}
                opacity={i === 0 || isHighlight ? 1 : 0.55}
              />
              <text className="chart-value" x={LABEL_W + barW + 6} y={y + 14}>
                {formatMetricValue(
                  metric === 'valueScore' ? 'price' : metric,
                  { value: entry.value, unavailableReason: null },
                  currency,
                  entry.row.metrics.packageKind,
                ).replace(/^.*?(?=\d)/, (m) => (metric === 'valueScore' ? '' : m))}
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption className="chart-caption">
        {metric === 'valueScore'
          ? 'Composite value score, 0–100, relative to these results.'
          : `${label} — ${METRICS[metric].formula} (${metricUnit(metric, currency)}). ${
              direction === 'lower' ? 'Lower is better.' : 'Higher is better.'
            }`}
        {missing > 0 &&
          ` ${missing} product${missing === 1 ? '' : 's'} omitted: this metric could not be calculated.`}
      </figcaption>
    </figure>
  );
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}
